import { map, sortBy, uniqBy } from "lodash";
import { useIntl } from "react-intl";
import { Dropdown, Grid, Icon, Image } from "semantic-ui-react";
import Highlighter from "react-highlight-words";

import * as DTO from "../../../../interfaces";
import { PostReviewEvent, ReviewStatus, SocialNetworkPostStatus, SourceType, WebComponents } from "@/interfaces";
import { fullNameFrom, useAccountSettings, printBasicDate, printDateTime, URLS } from "@/utils";
import { MediaPlayer } from "@/components/MediaPlayer";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";

import { descriptors, PostType } from "./descriptors";
import { PostHeader, ReviewBy, ReviewDate, SmallTitle } from "./style";

import "./styles.scss";
import { Svg } from "@/assets";
import React, { FC, useEffect, useMemo, useState } from "react";
import { reviewStatusOptions } from "../options";
import { SimpleStepper } from "@/components/SimpleStepper";
import { PostDetails, PostSocialNetworkDetails } from "@/services/bedrock";
import { SocialNetworksId } from "@/interfaces";

interface OwnProps {
  post: PostDetails;
  player?: DTO.PlayerAccount;
  activeSocialNetworks: DTO.SocialNetworksId[];
  changeReviewStatus: (status: ReviewStatus, postSnUrls: string[]) => void;
  onShowHistory: (events: PostReviewEvent[]) => void;
  showVideoPageModal: (pageId: number) => void;
}

interface PostSource {
  label: string;
  link?: string;
  onClick?: () => {};
}

type Props = OwnProps;

const PostFeedCellFC: FC<Props> = ({
  post: {
    id: postId,
    sourceId,
    sourceType,
    postSocialNetworks,
    media,
    keyphrases,
    reviewStatus,
    reviewStatusUpdatedDate,
    reviewer,
    postReviewEvents,
  },
  player,
  activeSocialNetworks,
  changeReviewStatus,
  onShowHistory,
  showVideoPageModal,
}: Props) => {
  const { formatMessage } = useIntl();
  const [reviewStatusLoading, setReviewStatusLoading] = useState<boolean>(false);

  const postedPosts: PostSocialNetworkDetails[] = useMemo(() => {
    return postSocialNetworks.filter(psn => {
      return psn.status === SocialNetworkPostStatus.POSTED;
    });
  }, [postSocialNetworks]);
  const postedNetworkIds = postedPosts.map(sn => sn.socialNetworkId);
  const maxPostedDate = postedPosts.map(p => p.postedTimestamp).sort()[0];

  const postSnUrls = useMemo(() => {
    return postedPosts?.reduce((acc, sn) => {
      const rawUrl = sn.externalPostUrl || "";
      const cleanUrl = sn.socialNetworkId !== "FB" ? rawUrl.replace(/[[\]"]/g, "") : rawUrl;

      return { ...acc, [sn.socialNetworkId]: cleanUrl };
    }, {});
  }, [postedPosts]);

  const [selectedSocialNetwork, setSelectedSocialNetwork] = useState<SocialNetworksId | undefined>(
    postedPosts[0]?.socialNetworkId
  );
  const postedSocialNetworkOptions = useMemo(
    () =>
      map(postedPosts, sn => ({
        key: sn.socialNetworkId,
        text: DTO.socialNetworkNames[sn.socialNetworkId],
        value: sn.socialNetworkId,
      })),
    [postedPosts]
  );

  const { captionWarning, transcriptWarning } = useMemo(() => {
    let cWarn = false;
    let tWarn = false;
    keyphrases.forEach(k => {
      if (["CAPTION", "HASHTAGS"].includes(k.location)) cWarn = true;
      if (k.location === "TRANSCRIPT") tWarn = true;
    });
    return { captionWarning: cWarn, transcriptWarning: tWarn };
  }, [keyphrases]);

  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.COMPLIANCE);
  const isSCPage = sourceType === "CONTENT_PAGE";

  const networksToShow: DTO.SocialNetworksId[] = useMemo(() => {
    if (isSCPage) {
      return ["SC"];
    } else return activeSocialNetworks;
  }, [activeSocialNetworks]);

  const [mediaIndex, setMediaIndex] = useState<number>(0);

  const primaryMedia = useMemo(() => {
    const mediaList = media?.sort((m1, m2) => m1.sortOrder - m2.sortOrder);
    if (mediaList && mediaList.length > 0) {
      return mediaList[mediaIndex];
    }
    return undefined;
  }, [media, mediaIndex]);

  const displayedTranscripts = useMemo(() => {
    if (!primaryMedia?.transcripts) return [];
    const sortedTranscripts = sortBy(primaryMedia.transcripts, t => (t.id ? -t.id : 0));
    const latestByLanguage = uniqBy(sortedTranscripts, "languageCode");
    return sortBy(latestByLanguage, t => (t.languageCode === "en-US" ? 0 : 1));
  }, [primaryMedia?.transcripts]);

  const transcriptOptions = useMemo(() => {
    return displayedTranscripts.map(t => {
      let label = t.languageCode === "en-US" ? "English" : t.languageCode == "es-US" ? "Spanish" : t.languageCode;
      label += t.isOriginal ? " (Original)" : " (Translation)";
      return {
        key: t.id,
        text: label,
        value: t.id,
      };
    });
  }, [displayedTranscripts]);

  const [transcriptId, setTranscriptId] = useState<number | undefined>(displayedTranscripts[0]?.id);
  const transcript = useMemo(() => {
    return primaryMedia?.transcripts?.find(t => t.id === transcriptId);
  }, [primaryMedia?.transcripts, transcriptId]);

  const transcriptLabel = useMemo(() => {
    if (primaryMedia?.category === "VIDEO") {
      return "Video Transcript";
    } else {
      return "Image Content";
    }
  }, [primaryMedia?.category]);

  const needsReview: boolean = useMemo(() => {
    return (
      reviewStatus != ReviewStatus.REVIEWED &&
      reviewStatus != ReviewStatus.COMPLETED &&
      (captionWarning || transcriptWarning)
    );
  }, [captionWarning, transcriptWarning, reviewStatus]);

  const playerName = useMemo(() => {
    return player ? fullNameFrom(player) : "Player";
  }, [player]);

  const postSource: PostSource = useMemo(() => {
    switch (sourceType) {
      case SourceType.CAMPAIGN:
        return {
          label: "SocialCoach Campaign",
          link: URLS.coach.editCampaigns.replace(":campaignId", sourceId.toString()),
        } as PostSource;
      case SourceType.CONTENT_PAGE:
        return {
          label: "Video Page",
          onClick: () => {
            showVideoPageModal(sourceId);
          },
        } as PostSource;
      case SourceType.VIDEO_PROJECT:
        return {
          label: "Video Project",
          link: URLS.coach.videoProject.replace(":id", sourceId.toString()),
        } as PostSource;
      case SourceType.PROMPT:
        return {
          label: "SocialCoach Prompt",
          link: URLS.coach.prompt.replace(":promptId", sourceId.toString()),
        } as PostSource;
      case SourceType.POST:
        return {
          label: `Posted by ${playerName}`,
        } as PostSource;
    }
  }, [playerName, sourceId, sourceType, showVideoPageModal]);

  useEffect(() => {
    setReviewStatusLoading(false);
  }, [reviewStatus]);

  const reviewStatusSection = () => {
    return (
      <>
        <Dropdown
          placeholder="Review Status"
          fluid
          defaultValue={undefined}
          selectOnNavigation={false}
          wrapSelection={false}
          selectOnBlur={false}
          loading={reviewStatusLoading}
          onChange={(_, data) => {
            setReviewStatusLoading(true);
            changeReviewStatus(data.value as ReviewStatus, Object.values(postSnUrls));
          }}
          selection
          value={reviewStatus}
          options={reviewStatusOptions.filter(
            o => o.value !== ReviewStatus.NULL && o.value !== ReviewStatus.CHANGES_SUBMITTED
          )}
          disabled={reviewStatus === ReviewStatus.COMPLETED || !withWritePermission}
        />
        {reviewStatusUpdatedDate && (
          <ReviewDate>
            {formatMessage({ ...descriptors[PostType.requestedDate] }) + printBasicDate(reviewStatusUpdatedDate)}
          </ReviewDate>
        )}
        {reviewer && <ReviewBy>{formatMessage({ ...descriptors[PostType.requestedBy] }) + reviewer.fullName}</ReviewBy>}
        {postReviewEvents && postReviewEvents.length > 0 && (
          <a className={"reviewHistoryLink"} onClick={() => onShowHistory(postReviewEvents)}>
            View Request History
          </a>
        )}
      </>
    );
  };

  const finalMessage = useMemo(() => {
    const socialPost = postedPosts.find(sn => {
      return sn.socialNetworkId === selectedSocialNetwork;
    });

    return socialPost?.postedCaption;
  }, [postedPosts, selectedSocialNetwork]);

  const postedSocialNetworksSection = () => {
    return (
      <Dropdown
        inline
        defaultValue={postedSocialNetworkOptions[0]?.value}
        selectOnNavigation={false}
        wrapSelection={false}
        selectOnBlur={false}
        onChange={(_, data) => {
          setSelectedSocialNetwork(`${data.value}` as SocialNetworksId);
        }}
        options={postedSocialNetworkOptions}
      />
    );
  };

  return (
    <Grid
      divided="vertically"
      columns={3}
      data-elm-id="postListElement"
      key={postId}
      className={`postFeedCell ${needsReview ? "withWarning" : ""}`}
    >
      <Grid.Row columns={1} className={"noPadding"}>
        <Grid.Column>
          <Grid.Row className={"postHeader"}>
            <Image circular size="mini" src={player?.photoUrl || Svg.noImageuser} className={"profileImage"} />
            <div>
              <SmallTitle>{playerName}</SmallTitle>
              <hr />
              <PostHeader>{maxPostedDate && printDateTime(maxPostedDate!!)}</PostHeader>
            </div>
          </Grid.Row>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={3} className={"noPadding"}>
        <Grid.Column width={3}>
          <Grid.Row>{primaryMedia && <MediaPlayer media={primaryMedia} />}</Grid.Row>
          {media?.length && media?.length > 1 && (
            <Grid.Row>
              <SimpleStepper index={mediaIndex} onUpdate={setMediaIndex} total={media.length} />
            </Grid.Row>
          )}
        </Grid.Column>

        {/* Captions  */}
        <Grid.Column width={8}>
          {!isSCPage && (
            <>
              <Grid.Row className={"transcriptHeader"}>
                <SmallTitle>
                  Caption
                  {captionWarning && <Icon name={"warning sign"} color={"orange"} className={"warningIcon"} />}
                </SmallTitle>
                {postedSocialNetworksSection()}
              </Grid.Row>
              <Grid.Row className={"captionContainer"}>
                <Highlighter
                  searchWords={keyphrases.map(k => k.content)}
                  textToHighlight={finalMessage || ""}
                  highlightClassName={"flaggedHighlight"}
                  style={{ whiteSpace: "pre-wrap" }}
                />
              </Grid.Row>
            </>
          )}

          {displayedTranscripts.length > 0 && (
            <>
              <Grid.Row className={"secondarySectionHeader transcriptHeader"}>
                <SmallTitle>
                  {transcriptLabel}
                  {transcriptWarning && <Icon name={"warning sign"} color={"orange"} className={"warningIcon"} />}
                </SmallTitle>
                {displayedTranscripts.length > 1 && (
                  <Dropdown
                    inline
                    options={transcriptOptions}
                    defaultValue={transcriptOptions[0]?.value}
                    onChange={(_, data) => setTranscriptId(data.value as number)}
                  />
                )}
              </Grid.Row>
              <Grid.Row className={"captionContainer"}>
                <Highlighter
                  searchWords={keyphrases.map(k => k.content)}
                  textToHighlight={transcript?.content || ""}
                  highlightClassName={"flaggedHighlight"}
                />
              </Grid.Row>
            </>
          )}
        </Grid.Column>

        {/* Review section  */}
        <Grid.Column width={3}>
          <Grid.Row>
            <SmallTitle>Social Network Links</SmallTitle>
          </Grid.Row>
          <Grid.Row>
            <SocialNetworksSection actives={postedNetworkIds} links={postSnUrls} allSocialNetworks={networksToShow} />
          </Grid.Row>
          <Grid.Row className={"secondarySectionHeader"}>
            <SmallTitle>Source</SmallTitle>
          </Grid.Row>
          <Grid.Row>
            {(postSource.link || postSource.onClick) && (
              <a href={postSource.link} onClick={postSource.onClick} target="_blank" className={"promptLink"}>
                {postSource.label}
              </a>
            )}
            {!(postSource.link || postSource.onClick) && postSource.label}
          </Grid.Row>
          <Grid.Row className={"secondarySectionHeader"}>
            <SmallTitle>Review Status</SmallTitle>
          </Grid.Row>
          <Grid.Row>{reviewStatusSection()}</Grid.Row>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export const PostFeedCell = PostFeedCellFC;
